import { create } from 'zustand';
import { generateTambolaTicket } from '../utils/ticketGenerator.js';
import { audio } from '../utils/audio.js';
import { useRoomStore } from './room.store.js';

interface ActivityLogItem {
  id: string;
  text: string;
  time: string;
}

interface LeaderboardItem {
  rank: number;
  name: string;
  prizesClaimed: number;
  score: number;
  isOut: boolean;
}

interface WinnerAnnouncementState {
  id: string;
  winnerName: string;
  prizeType: string;
}

interface FloatingReaction {
  id: string;
  emoji: string;
  sender: string;
  xOffset: number; // random offset for organic spread
  delay: number; // staggered animations
}

interface GameState {
  ticketGrid: number[][];
  gameStatus: 'active' | 'paused' | 'completed' | 'countdown';
  calledNumbers: number[];
  currentNumber: number | null;
  previousNumber: number | null;
  markedNumbers: number[];
  prizes: Record<string, { claimed: boolean; winner: string | null }>;
  activityLog: ActivityLogItem[];
  leaderboard: LeaderboardItem[];
  gameTimer: number; // in seconds
  currentNumberTime: number; // timestamp when current number was called
  isOut: boolean; // is current player disqualified
  claimingPrizeType: string | null; // active loading spinner for prize claim
  reactions: FloatingReaction[];
  winnerQueue: WinnerAnnouncementState[];
  activeAnnouncement: WinnerAnnouncementState | null;
  countdownValue: number;

  startGame: () => void;
  stopGame: () => void;
  togglePause: () => void;
  markNumber: (num: number) => void;
  claimPrize: (prizeType: string, playerName: string) => void;
  sendEmojiReaction: (emoji: string) => void;
  triggerWinnerAnnouncement: (winnerName: string, prizeType: string) => void;
  processAnnouncementQueue: () => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  ticketGrid: generateTambolaTicket(),
  gameStatus: 'active',
  calledNumbers: [],
  currentNumber: null,
  previousNumber: null,
  markedNumbers: [],
  prizes: {
    'Early Five': { claimed: false, winner: null },
    'Top Line': { claimed: false, winner: null },
    'Middle Line': { claimed: false, winner: null },
    'Bottom Line': { claimed: false, winner: null },
    'Four Corners': { claimed: false, winner: null },
    'Full House': { claimed: false, winner: null },
  },
  winnerQueue: [],
  activeAnnouncement: null,
  activityLog: [],
  leaderboard: [],
  gameTimer: 0,
  isOut: false,
  claimingPrizeType: null,
  reactions: [],
  currentNumberTime: 0,
  countdownValue: 0,

  startGame: () => {
    const ticketGrid = generateTambolaTicket();
    set({
      ticketGrid,
      gameStatus: 'active',
      gameTimer: 0,
      calledNumbers: [],
      currentNumber: null,
      previousNumber: null,
      markedNumbers: [],
      winnerQueue: [],
      activeAnnouncement: null,
      isOut: false,
      claimingPrizeType: null,
      reactions: [],
      currentNumberTime: 0,
      countdownValue: 0,
      prizes: {
        'Early Five': { claimed: false, winner: null },
        'Top Line': { claimed: false, winner: null },
        'Middle Line': { claimed: false, winner: null },
        'Bottom Line': { claimed: false, winner: null },
        'Four Corners': { claimed: false, winner: null },
        'Full House': { claimed: false, winner: null },
      },
    });

    const activeRoom = useRoomStore.getState().activeRoom;
    if (activeRoom) {
      activeRoom.send('register-ticket', { ticketGrid });

      activeRoom.onMessage('room-sync', (payload: any) => {
        const myData = payload.players.find((p: any) => p.id === activeRoom.sessionId);
        const amIOut = myData ? myData.isOut : false;

        const serverLeaderboard = (payload.game?.leaderboard || []).map((item: any) => ({
          rank: item.rank,
          name: item.name,
          prizesClaimed: Object.values(payload.game?.prizes || {}).filter((p: any) => p.winner === item.name).length,
          score: item.score,
          isOut: item.isOut,
        }));

        const prevCountdown = get().countdownValue;
        const newCountdown = payload.roomDetails.countdownValue || 0;
        const prevStatus = get().gameStatus;
        const newStatus = payload.roomDetails.gameStatus;

        // Play visual/audio cues based on countdown ticking
        if (newStatus === 'countdown' && newCountdown !== prevCountdown) {
          if (newCountdown > 0) {
            try {
              audio.playTick();
            } catch {}
          }
        } else if (newStatus === 'active' && prevStatus === 'countdown') {
          try {
            audio.playGo();
          } catch {}
        }

        const nextState: any = {
          gameStatus: newStatus,
          gameTimer: payload.roomDetails.gameTimer,
          countdownValue: newCountdown,
          prizes: payload.game?.prizes || get().prizes,
          leaderboard: serverLeaderboard,
          calledNumbers: payload.game?.calledNumbers || [],
          currentNumber: payload.game?.currentNumber,
          previousNumber: payload.game?.previousNumber,
          currentNumberTime: payload.game?.currentNumberTime || 0,
          isOut: amIOut,
        };

        if (payload.myTicket) {
          nextState.ticketGrid = payload.myTicket;
        }
        if (payload.myMarkedNumbers) {
          nextState.markedNumbers = payload.myMarkedNumbers;
        }

        set(nextState);
      });

      activeRoom.onMessage('next-number', (data: { number: number; previous: number | null; calledNumbers: number[]; timestamp: number }) => {
        set({
          currentNumber: data.number,
          previousNumber: data.previous,
          calledNumbers: data.calledNumbers,
          currentNumberTime: data.timestamp,
        });

        try {
          audio.playBallCalled();
          audio.speak(`Number ${data.number}`);
        } catch (e) {
          console.warn('SpeechSynthesis or AudioContext blocked:', e);
        }
      });

      activeRoom.onMessage('emoji-reaction', (data: { displayName: string; emoji: string }) => {
        const newReaction = {
          id: Math.random().toString(),
          emoji: data.emoji,
          sender: data.displayName,
          xOffset: Math.random() * 80 - 40, // random spread left/right (-40px to +40px)
          delay: Math.random() * 0.2, // stagger animation start
        };

        set((state) => ({ reactions: [...state.reactions, newReaction] }));

        // Clear after animation finishes (2.5 seconds)
        setTimeout(() => {
          set((state) => ({
            reactions: state.reactions.filter((r) => r.id !== newReaction.id),
          }));
        }, 2500);
      });

      activeRoom.onMessage('prize-claimed', (data: { playerId: string; displayName: string; prizeType: string; success: boolean; errorReason?: string }) => {
        const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        
        if (data.playerId === activeRoom.sessionId) {
          set({ claimingPrizeType: null });
          if (data.success) {
            try {
              audio.playSuccess();
            } catch {}
            get().triggerWinnerAnnouncement(data.displayName, data.prizeType);
          } else {
            try {
              audio.playError();
            } catch {}
            set({ isOut: true });
            alert(data.errorReason || 'Check failed. You are OUT!');
          }
        } else {
          if (data.success) {
            try {
              audio.playSuccess();
            } catch {}
            get().triggerWinnerAnnouncement(data.displayName, data.prizeType);
            set((state) => ({
              activityLog: [
                { id: Math.random().toString(), text: `${data.displayName} claimed ${data.prizeType}!`, time: timeStr },
                ...state.activityLog.slice(0, 4),
              ],
            }));
          }
        }
      });

      activeRoom.onMessage('player-out', (data: { playerId: string; displayName: string; reason: string }) => {
        const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        
        set((state) => ({
          activityLog: [
            { id: Math.random().toString(), text: `🚨 ${data.displayName} is OUT: ${data.reason}`, time: timeStr },
            ...state.activityLog.slice(0, 4),
          ],
        }));

        if (data.playerId === activeRoom.sessionId) {
          try {
            audio.playError();
          } catch {}
          set({ isOut: true });
        }
      });
    }
  },

  stopGame: () => {
    // Handled by server
  },

  togglePause: () => {
    const activeRoom = useRoomStore.getState().activeRoom;
    if (activeRoom) {
      activeRoom.send('toggle-pause', {});
    }
  },

  markNumber: (num) => {
    const { calledNumbers, markedNumbers, isOut, currentNumber } = get();
    if (isOut) return;

    const roomDetails = useRoomStore.getState().roomDetails;

    if (num !== currentNumber && calledNumbers.includes(num)) {
      if (roomDetails && roomDetails.allowLateMarking === false) {
        try {
          audio.playError();
        } catch {}
        alert("Late marking is disabled in this room! You can only mark the current active number.");
        return;
      }
    }

    if (!calledNumbers.includes(num)) {
      const roomDetails = useRoomStore.getState().roomDetails;
      const shouldDisqualify = roomDetails ? roomDetails.disqualifyOnWrongClick !== false : true;

      try {
        audio.playError();
      } catch {}
      
      if (shouldDisqualify) {
        set({ isOut: true });
        const activeRoom = useRoomStore.getState().activeRoom;
        if (activeRoom) {
          activeRoom.send('mark-cell', { number: num });
        }
      }
      return;
    }

    const activeRoom = useRoomStore.getState().activeRoom;
    if (activeRoom) {
      activeRoom.send('mark-cell', { number: num });
    }

    try {
      audio.playMark();
    } catch {}

    if (markedNumbers.includes(num)) {
      set({ markedNumbers: markedNumbers.filter((n) => n !== num) });
    } else {
      set({ markedNumbers: [...markedNumbers, num] });
    }
  },

  claimPrize: (prizeType) => {
    const { isOut, claimingPrizeType } = get();
    if (isOut || claimingPrizeType) return;

    const activeRoom = useRoomStore.getState().activeRoom;
    if (activeRoom) {
      set({ claimingPrizeType: prizeType });
      activeRoom.send('claim-prize', { prizeType });
    }
  },

  sendEmojiReaction: (emoji) => {
    const activeRoom = useRoomStore.getState().activeRoom;
    if (activeRoom) {
      activeRoom.send('emoji-reaction', { emoji });
    }
  },

  triggerWinnerAnnouncement: (winnerName, prizeType) => {
    const newWinner = {
      id: Math.random().toString(),
      winnerName,
      prizeType,
    };
    set((state) => ({
      winnerQueue: [...state.winnerQueue, newWinner],
    }));

    if (get().activeAnnouncement === null) {
      get().processAnnouncementQueue();
    }
  },

  processAnnouncementQueue: () => {
    const { winnerQueue } = get();
    if (winnerQueue.length === 0) {
      set({ activeAnnouncement: null });
      return;
    }

    const nextWinner = winnerQueue[0];
    if (nextWinner) {
      set((state) => ({
        activeAnnouncement: nextWinner,
        winnerQueue: state.winnerQueue.slice(1),
      }));

      // Toast auto-closes after 2.5 seconds (shorter and non-blocking now)
      setTimeout(() => {
        set({ activeAnnouncement: null });
        setTimeout(() => {
          get().processAnnouncementQueue();
        }, 500);
      }, 2500);
    }
  },
}));
export type { ActivityLogItem, LeaderboardItem, FloatingReaction };
